'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

/**
 * Drives the mobile menu->recipe->ingredient stacked-sheet UI purely from
 * `?recipe=&ingredient=` searchParams, so a deep link reconstructs the whole
 * stack on a fresh page load and the back button pops one level at a time.
 * Opening pushes a history entry; closing replaces (no history spam).
 */
export function useSheetStackParams() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const recipeId = searchParams.get('recipe');
  const ingredientId = searchParams.get('ingredient');

  const buildUrl = useCallback(
    (next: { recipe?: string | null; ingredient?: string | null }) => {
      const params = new URLSearchParams(searchParams.toString());
      if (next.recipe !== undefined) {
        if (next.recipe === null) {
          params.delete('recipe');
        } else {
          params.set('recipe', next.recipe);
        }
      }
      if (next.ingredient !== undefined) {
        if (next.ingredient === null) {
          params.delete('ingredient');
        } else {
          params.set('ingredient', next.ingredient);
        }
      }
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [pathname, searchParams]
  );

  const openRecipe = useCallback(
    (id: string) => router.push(buildUrl({ recipe: id, ingredient: null })),
    [router, buildUrl]
  );

  const openIngredient = useCallback(
    (id: string) => router.push(buildUrl({ ingredient: id })),
    [router, buildUrl]
  );

  const closeIngredient = useCallback(
    () => router.replace(buildUrl({ ingredient: null }), { scroll: false }),
    [router, buildUrl]
  );

  const closeRecipe = useCallback(
    () =>
      router.replace(buildUrl({ recipe: null, ingredient: null }), {
        scroll: false
      }),
    [router, buildUrl]
  );

  return {
    recipeId,
    ingredientId,
    openRecipe,
    openIngredient,
    closeIngredient,
    closeRecipe
  };
}
